import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DashboardLayout } from '../../components/DashboardLayout';
import { TestTube, Play, CheckCircle, Coins, TrendingUp, Clock, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

export const TestBilling = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchLocations(selectedCustomer);
    }
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get all customers from database
      const response = await axios.get(`${API}/api/superadmin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/locations/by-customer`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { customer_id: customerId }
      });
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0].id);
      } else {
        setSelectedLocation(null);
        toast.warning('Dieser Kunde hat keine Standorte');
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Fehler beim Laden der Standorte');
      setLocations([]);
      setSelectedLocation(null);
    }
  };

  const simulatePayment = async () => {
    if (!selectedCustomer || !selectedLocation) {
      toast.error('Bitte wähle einen Kunden und Standort aus');
      return;
    }

    const location = locations.find(l => l.id === selectedLocation);
    if (!location) {
      toast.error('Standort nicht gefunden');
      return;
    }

    setSimulating(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/billing/simulate-payment`,
        {
          customer_id: selectedCustomer,
          location_id: selectedLocation,
          amount: location.total_price_net || 19.99
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      
      // Show success toast with details
      let message = `Zahlung simuliert: ${response.data.amount.toFixed(2)}€`;
      if (response.data.referral_credit) {
        message += ` | Referral-Credit: ${response.data.referral_credit}€`;
      }
      if (response.data.affiliate_commission) {
        message += ` | Affiliate: ${response.data.affiliate_commission.toFixed(2)}€`;
      }
      
      toast.success(message);
      
      // Refresh data
      await fetchCustomers();
      
    } catch (error) {
      console.error('Error simulating payment:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Simulation');
    } finally {
      setSimulating(false);
    }
  };

  const selectedCustomerData = customers.find(c => c.id === selectedCustomer);
  const selectedLocationData = locations.find(l => l.id === selectedLocation);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <TestTube className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
            Zahlungs-Simulation
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Teste Referral-Credits und Affiliate-Provisionen ohne echte Zahlungen
          </p>
        </div>

        {/* Warning Banner */}
        <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <TestTube className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                Test-Modus
              </p>
              <p className="text-xs sm:text-sm text-orange-800 dark:text-orange-200 mt-1">
                Diese Funktion simuliert Zahlungen für Testing-Zwecke. Keine echten Zahlungen werden verarbeitet.
                Credits und Provisionen werden trotzdem berechnet und in der Datenbank gespeichert.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Left Column - Configuration */}
          <div className="space-y-6">
            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">1. Kunde auswählen</CardTitle>
                <CardDescription>Wähle den Kunden, der eine Zahlung simulieren soll</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Kunde</label>
                  <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                    <SelectTrigger>
                      <SelectValue placeholder="Kunde auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company_name} ({customer.customer_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCustomerData && (
                  <div className="p-3 bg-muted rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Referral-Code:</span>
                      <span className="font-mono font-semibold">{selectedCustomerData.referral_code || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Aktuelles Guthaben:</span>
                      <span className="font-semibold">{selectedCustomerData.referral_credits?.toFixed(2) || '0.00'}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Geworben von:</span>
                      <span>{selectedCustomerData.referred_by || '-'}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Selection */}
            {selectedCustomer && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">2. Standort auswählen</CardTitle>
                  <CardDescription>Wähle den Standort für die Zahlung</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Standort</label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger>
                        <SelectValue placeholder="Standort auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name} - {location.total_price_net?.toFixed(2)}€
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedLocationData && (
                    <div className="p-3 bg-muted rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Mitarbeiter:</span>
                        <span>{selectedLocationData.employee_count || 10}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Abrechnungszyklus:</span>
                        <span className="font-semibold">Monat {selectedLocationData.billing_cycle_month || 1}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Betrag:</span>
                        <span className="font-bold text-lg">{selectedLocationData.total_price_net?.toFixed(2) || '19.99'}€</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Simulate Button */}
            {selectedCustomer && selectedLocation && (
              <Button
                onClick={simulatePayment}
                disabled={simulating}
                className="w-full"
                size="lg"
              >
                {simulating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Simuliere Zahlung...
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Zahlung simulieren
                  </>
                )}
              </Button>
            )}
            
            {/* No Locations Warning */}
            {selectedCustomer && locations.length === 0 && (
              <Card className="border-2 border-orange-200 dark:border-orange-800">
                <CardContent className="py-8">
                  <div className="text-center space-y-3">
                    <div className="flex justify-center">
                      <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full">
                        <Clock className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                      Keine Standorte vorhanden
                    </h3>
                    <p className="text-sm text-orange-800 dark:text-orange-200 max-w-md mx-auto">
                      Dieser Kunde hat noch keine Standorte. Bitte erstelle zuerst einen Standort für diesen Kunden.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Result */}
          <div className="space-y-6">
            {result ? (
              <>
                <Card className="border-2 border-green-200 dark:border-green-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Simulation erfolgreich
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                        <p className="text-xs text-blue-600 dark:text-blue-400">Betrag</p>
                        <p className="text-xl font-bold">{result.amount?.toFixed(2)}€</p>
                      </div>
                      <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                        <p className="text-xs text-purple-600 dark:text-purple-400">Neuer Zyklus</p>
                        <p className="text-xl font-bold">Monat {result.new_cycle}</p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-2 border-t">
                      {result.referral_credit && (
                        <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Coins className="h-5 w-5 text-green-600 dark:text-green-400" />
                            <div>
                              <p className="font-medium text-sm">Referral-Credit vergeben</p>
                              <p className="text-xs text-muted-foreground">An Werber gutgeschrieben</p>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-green-600">+{result.referral_credit}€</span>
                        </div>
                      )}

                      {result.affiliate_commission && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            <div>
                              <p className="font-medium text-sm">Affiliate-Provision</p>
                              <p className="text-xs text-muted-foreground">Berechnet und gespeichert</p>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-blue-600">+{result.affiliate_commission?.toFixed(2)}€</span>
                        </div>
                      )}

                      {!result.referral_credit && !result.affiliate_commission && (
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                          <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Keine Referral-Credits oder Affiliate-Provisionen für diesen Zyklus
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="p-3 bg-muted rounded-lg text-xs space-y-1">
                      <p><strong>Info:</strong></p>
                      <p>• Referral-Credits werden ab dem 2. Monat vergeben</p>
                      <p>• Affiliate-Provisionen werden jeden Monat berechnet</p>
                      <p>• Credits werden automatisch von nächster Rechnung abgezogen</p>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center space-y-3">
                    <div className="flex justify-center">
                      <div className="p-4 bg-muted rounded-full">
                        <Users className="h-10 w-10 text-muted-foreground" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold">Keine Simulation durchgeführt</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Wähle einen Kunden und Standort aus und klicke auf "Zahlung simulieren"
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TestBilling;
